/**
 * The website box's way to the web: truman reviews every request, and this
 * transport carries the one it is given to HeyDex's network Worker
 * with a token Cloudflare grants the signed-in reader.
 * A rejection's message is what the box's command prints.
 */
import type { HostRequest, HostResponse, HostTransport } from "@heydaytime/truman-web";
import { insideHeyDayNewsApp, boxInApp } from "../appMode";
import { appNetworkTransport } from "./app-network";

/** The Worker's origin and a token for it, valid until `expiresAt` (Unix seconds). */
interface Grant {
  readonly url: string;
  readonly token: string;
  readonly expiresAt: number;
}

/** A token this close to its end is replaced before use. */
const RENEW_BEFORE_MS = 5 * 60_000;

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function strings(value: unknown): Record<string, string> | null {
  const fields = record(value);
  return fields && Object.values(fields).every((entry) => typeof entry === "string")
    ? (fields as Record<string, string>)
    : null;
}

/** The message of an `{error: {message}}` answer. */
function errorMessage(value: unknown): string | null {
  const message = record(record(value)?.["error"])?.["message"];
  return typeof message === "string" && message.length > 0 ? message : null;
}

async function failure(response: Response, who: string): Promise<Error> {
  const answer: unknown = await response.json().catch(() => null);
  return new Error(errorMessage(answer) ?? `${who} answered ${response.status}`);
}

async function requestGrant(): Promise<Grant> {
  const response = await fetch("/api/heydex/network", {
    method: "POST",
    credentials: "same-origin",
  });
  if (!response.ok) throw await failure(response, "heyday.news's network grant");
  const fields = record(await response.json());
  const url = fields?.["url"];
  const token = fields?.["token"];
  const expiresAt = fields?.["expiresAt"];
  if (
    typeof url !== "string" ||
    !URL.canParse(url) ||
    typeof token !== "string" ||
    typeof expiresAt !== "number"
  ) {
    throw new Error("heyday.news's network grant is malformed");
  }
  return { url: url.replace(/\/+$/, ""), token, expiresAt };
}

function addresses(value: unknown): { address: string; family: 4 | 6 }[] {
  const list = record(value)?.["addresses"];
  if (!Array.isArray(list)) throw new Error("HeyDex's network answered a malformed resolution");
  return list.map((item: unknown) => {
    const fields = record(item);
    const address = fields?.["address"];
    const family = fields?.["family"];
    if (typeof address !== "string" || (family !== 4 && family !== 6)) {
      throw new Error("HeyDex's network answered a malformed resolution");
    }
    return { address, family };
  });
}

function hostResponse(value: unknown): HostResponse {
  const fields = record(value);
  const status = fields?.["status"];
  const headers = strings(fields?.["headers"]);
  const body = fields?.["body"];
  if (typeof status !== "number" || !headers || typeof body !== "string") {
    throw new Error("HeyDex's network answered a malformed response");
  }
  return { status, headers, body };
}

function browserNetworkTransport(): HostTransport {
  let held: Grant | null = null;
  let renewing: Promise<Grant> | null = null;

  /** A usable grant; `refused` is a token the Worker just turned away. */
  const grant = (refused?: string): Promise<Grant> => {
    if (held && held.token !== refused && held.expiresAt * 1000 - Date.now() > RENEW_BEFORE_MS) {
      return Promise.resolve(held);
    }
    renewing ??= requestGrant()
      .then((fresh) => {
        held = fresh;
        return fresh;
      })
      .finally(() => {
        renewing = null;
      });
    return renewing;
  };

  const call = async (path: "resolve" | "request", body: unknown): Promise<unknown> => {
    let current = await grant();
    for (let attempt = 0; ; attempt++) {
      const response = await fetch(`${current.url}/${path}`, {
        method: "POST",
        headers: { authorization: `Bearer ${current.token}`, "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (response.status === 401 && attempt === 0) {
        current = await grant(current.token);
        continue;
      }
      if (!response.ok) throw await failure(response, "HeyDex's network");
      return response.json();
    }
  };

  return {
    resolve: async (host) => addresses(await call("resolve", { host })),
    request: async (request: HostRequest) => hostResponse(await call("request", request)),
  };
}

/** Box ownership and network ownership differ on Android: its WASM box uses device networking. */
export async function networkTransport(): Promise<{ transport: HostTransport; close(): void }> {
  if (insideHeyDayNewsApp) {
    if (boxInApp) throw new Error("The native app's network belongs to its TrumanKit box");
    return appNetworkTransport();
  }
  return { transport: browserNetworkTransport(), close: () => {} };
}
